import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import { alpha, useTheme } from '@mui/material/styles';
import { getLoggedInUserProfile } from '../../../../apps/user/Users/UserService';
import { logout } from '../../../../auth/Login/AuthService';
import { getCartItemsCount } from '../../../../views/Ecommerce/Cart/CartService'; // Import the API function

interface Props {
  onSidebarOpen: () => void;
  pages: {
    landings: Array<PageItem>;
    company: Array<PageItem>;
    account: Array<PageItem>;
    secondary: Array<PageItem>;
    blog: Array<PageItem>;
    portfolio: Array<PageItem>;
  };
  colorInvert?: boolean;
}

const Topbar = ({ onSidebarOpen }: Props): JSX.Element => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userName, setUserName] = useState('');
  const [role, setRole] = useState('');
  const [cartItemCount, setCartItemCount] = useState<number>(0); // New state for cart count
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigator = useNavigate();
  const location = useLocation(); // To detect route changes
  const theme = useTheme();

  // Fetch user profile
  const fetchUserProfile = () => {
    getLoggedInUserProfile()
      .then((response) => {
        setIsLoggedIn(true);
        setUserName(`${response.data.firstName} ${response.data.lastName}`);

        const userRole =
          response.data.authorities && response.data.authorities[0]?.authority;
        setRole(userRole?.replace('ROLE_', '').toLowerCase() || '');
      })
      .catch(() => {
        setIsLoggedIn(false);
        setUserName('');
        setRole('');
      });
  };

  // Fetch cart count
  const fetchCartItemCount = () => {
    getCartItemsCount()
      .then((response) => {
        setCartItemCount(response.data); // Assuming the response contains the total quantity
      })
      .catch(() => {
        setCartItemCount(0); // Default to 0 if there's an error fetching the cart count
      });
  };

  // Fetch profile and cart count on component load, on route change, or when browser navigation key changes
  useEffect(() => {
    fetchUserProfile();
    if (isLoggedIn) {
      fetchCartItemCount(); // Fetch cart count when user is logged in
    }
  }, [location.key, isLoggedIn]); // Trigger fetch when route changes or login state changes

  const handleCartClick = (): void => {
    navigator('/ecommerce/cart');
  };

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (): void => {
    setAnchorEl(null);
  };

  const handleLogout = async (): Promise<void> => {
    await logout();
    setIsLoggedIn(false);
    setUserName('');
    setRole('');
    setAnchorEl(null);
    window.location.href = '/';
  };

  const getDashboardUrl = () => {
    switch (role) {
      case 'system':
        return '/system/dashboard/sections';
      case 'admin':
        return '/admin/dashboard';
      case 'manager':
        return '/manager/dashboard';
      case 'user':
        return '/user/home';
      default:
        return '/';
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      width={1}
    >
      <Box component="a" href="/" title="docket" width={{ xs: 100, md: 120 }}>
        <img src={'/assets/logo.png'} width={210} alt="Docket" />
      </Box>

      <Box sx={{ display: { xs: 'none', md: 'flex' } }} alignItems="center">
        <IconButton sx={{ marginRight: 2 }} onClick={handleCartClick}>
          <Badge badgeContent={cartItemCount} color="error">
            {' '}
            {/* Update badgeContent with cartItemCount */}
            <ShoppingCartOutlinedIcon sx={{ fontSize: 30 }} />
          </Badge>
        </IconButton>

        {isLoggedIn ? (
          <IconButton onClick={handleAvatarClick}>
            <AccountCircleOutlinedIcon sx={{ fontSize: 40 }} />
          </IconButton>
        ) : (
          <Box marginLeft={4}>
            <Button
              variant="contained"
              color="primary"
              href={'/login'}
              size="large"
            >
              Login
            </Button>
          </Box>
        )}
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem disabled>{userName}</MenuItem>
        <MenuItem component={Link} to={getDashboardUrl()}>
          Go to Dashboard
        </MenuItem>
        <MenuItem onClick={handleLogout}>Logout</MenuItem>
      </Menu>

      <Box sx={{ display: { xs: 'flex', md: 'none' } }} alignItems="center">
        <Button
          onClick={() => onSidebarOpen()}
          aria-label="Menu"
          variant="outlined"
          sx={{
            borderRadius: 2,
            minWidth: 'auto',
            padding: 1,
            borderColor: alpha(theme.palette.divider, 0.2),
          }}
        >
          <MenuIcon />
        </Button>
      </Box>
    </Box>
  );
};

export default Topbar;
